import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { homeFor } from '@/lib/home-route';
import { api } from '@/services/api';
import { AuthShell } from '@/components/auth/AuthShell';
import {
  AuthHeading,
  EditorialField,
  MonoLabel,
  PillButton,
  PillLink,
} from '@/components/editorial';
import { serverMessage } from '@/lib/serverMessage';

// Built per render locale (#2109 L6): the validation message is copy.
const makeForgotPasswordSchema = (t: TFunction<'auth'>) =>
  z.object({
    email: z.string().email(t('shared.invalidEmail')),
  });

type ForgotPasswordFormData = z.infer<ReturnType<typeof makeForgotPasswordSchema>>;

export function ForgotPasswordPage() {
  // B-169: like /login (B-168) and /apply (GH-271.1), this page only READS
  // auth state — no token paths, no AuthContext changes.
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [emailSent, setEmailSent] = useState(false);
  // Signed-in users may still need the email reset (the in-profile
  // change-password requires the CURRENT password) — this opts them past
  // the interstitial into the normal form.
  const [proceedAnyway, setProceedAnyway] = useState(false);
  // One-way latch: once the form has rendered on this mount, it stays — an
  // auth flip underneath (e.g. signing in from another tab) must not swap
  // the interstitial in over a form mid-use.
  const formShownRef = useRef(false);
  const { toast } = useToast();
  const { t } = useTranslation('auth');
  const forgotPasswordSchema = useMemo(() => makeForgotPasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await api.auth.forgotPassword(data.email);
      setEmailSent(true);
      toast({
        title: t('forgot.emailSentTitle'),
        description: t('forgot.emailSentDescription'),
      });
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: t('forgot.sendFailedTitle'),
        description: serverMessage(error, t('forgot.sendFailedDescription')),
      });
    }
  };

  // B-169: a signed-in user who reaches /forgot-password must see their
  // session acknowledged, never a bare recovery form implying they're signed
  // out (B-164/B-168 sibling). While auth state hydrates, show a neutral
  // spinner so neither the form nor the interstitial flashes early.
  if (!formShownRef.current) {
    if (loading) {
      return (
        <div
          className="flex min-h-screen items-center justify-center"
          role="status"
          aria-label={t('shared.checkingSession')}
        >
          <Loader2 className="h-6 w-6 animate-spin text-stevens-indigo" />
        </div>
      );
    }

    if (user && !proceedAnyway) {
      return (
        <AuthShell
          documentLabel={t('forgot.documentLabel')}
          meta={[{ k: t('shared.signedInAs'), v: user.email ?? t('shared.yourAccount') }]}
        >
          <header className="flex flex-col gap-4 mb-10">
            <MonoLabel>{t('shared.alreadySignedIn')}</MonoLabel>
            <AuthHeading>
              {t('shared.alreadySignedInTitle')}
            </AuthHeading>
            <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
              <Trans
                t={t}
                i18nKey="forgot.interstitialLede"
                values={{ email: user.email ?? t('shared.yourAccount') }}
                components={{ strong: <strong className="font-medium text-stevens-ink" /> }}
              />
            </p>
          </header>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <PillButton type="button" onClick={() => navigate(homeFor(user.role), { replace: true })}>
              {t('shared.goToMyAccount')} {"→"}
            </PillButton>
            <PillButton type="button" variant="ghost" onClick={() => setProceedAnyway(true)}>
              {t('forgot.resetAnyway')}
            </PillButton>
          </div>
        </AuthShell>
      );
    }
  }
  formShownRef.current = true;

  if (emailSent) {
    return (
      <AuthShell
        documentLabel={t('forgot.sentDocumentLabel')}
        meta={[{ k: t('forgot.sentToLabel'), v: getValues('email') }]}
      >
        <header className="flex flex-col gap-4 mb-8">
          <MonoLabel>{t('forgot.checkYourEmail')}</MonoLabel>
          <AuthHeading>
            {t('forgot.sentTitle')}
          </AuthHeading>
          <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
            <Trans
              t={t}
              i18nKey="forgot.sentLede"
              values={{ email: getValues('email') }}
              components={{ strong: <strong className="font-medium text-stevens-ink" /> }}
            />
          </p>
          <p className="text-body leading-[1.6] text-stevens-ink-3 max-w-[52ch] m-0">
            {t('forgot.sentSpamNote')}
          </p>
        </header>

        <div className="flex items-center gap-3 mt-2">
          <PillLink href="/login" className="no-underline">
            <ArrowLeft className="h-4 w-4" />
            {t('forgot.backToSignIn')}
          </PillLink>
          <button
            onClick={() => setEmailSent(false)}
            className="font-sans text-body text-stevens-ink-2 hover:text-stevens-ink transition-colors"
          >
            {t('forgot.sendDifferentAddress')}
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      documentLabel={t('forgot.documentLabel')}
      meta={[{ k: t('forgot.recoveryLabel'), v: t('forgot.recoveryValue') }]}
      alternate={{ prompt: t('forgot.alternatePrompt'), to: '/login', label: t('forgot.alternateLabel') }}
    >
      <header className="flex flex-col gap-4 mb-10">
        <MonoLabel>{t('forgot.eyebrow')}</MonoLabel>
        <AuthHeading>
          <Trans t={t} i18nKey="forgot.title" components={{ em: <em className="not-italic text-stevens-indigo" /> }} />
        </AuthHeading>
        <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
          {t('forgot.lede')}
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
        <EditorialField
          label={t('shared.emailLabel')}
          type="email"
          autoComplete="email"
          placeholder={t('shared.emailPlaceholder')}
          error={errors.email?.message}
          {...register('email')}
        />

        <div className="flex items-center gap-3 mt-2">
          <PillButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('forgot.sending')}
              </>
            ) : (
              <>{t('forgot.submit')} {"→"}</>
            )}
          </PillButton>
          <Link
            to="/login"
            className="font-sans text-body text-stevens-ink-2 hover:text-stevens-ink transition-colors inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('forgot.backToSignIn')}
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
